/**
 * "Zuschnitt anpassen": the original photo with the four corners the scan was cut along. Drag a
 * corner (finger or mouse), move it with the arrow keys, or keep the whole photo - then the scan
 * is made again from the original and the receipt read again.
 */
import { Crop, Maximize, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useFileUrl, useUpdateDocument } from "@/hooks/use-documents";
import type { DocumentRecord, ScanCorner } from "@/lib/api/documents";
import { rereadDocument } from "@/lib/api/extract";
import { CropFrame } from "@/components/capture/crop-frame";
import { cropState, startCorners } from "@/lib/capture/crop";

type Hints = { ownVatId?: string; ownIban?: string; ownNames?: string };

export function CropEditor({ document, hints }: { document: DocumentRecord; hints: Hints }) {
  const { t } = useTranslation();
  const updateDocument = useUpdateDocument();
  const pages = document.scan ?? [];
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [corners, setCorners] = useState<Array<ScanCorner[] | null>>([]);
  const [applying, setApplying] = useState(false);

  const page = pages[current];
  const name = document.originals?.[current];
  const { url } = useFileUrl(document, name ?? "", open);
  if (pages.length === 0 || !document.originals?.length) return null;

  const apply = async () => {
    setApplying(true);
    try {
      const { patch } = await rereadDocument(document, { kind: document.kind, ...hints }, corners);
      await updateDocument.mutateAsync({ id: document.id, input: patch });
      toast.success(t("crop.applied"));
      setOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("document.reExtractFailed"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setCurrent(0);
          setCorners(pages.map((p) => (p.corners ? startCorners(p) : null)));
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="crop-open">
          <Crop /> {t("crop.open")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("crop.title")}</DialogTitle>
          <DialogDescription>{t("crop.hint")}</DialogDescription>
        </DialogHeader>

        {pages.length > 1 && (
          <div className="flex gap-2">
            {pages.map((_, index) => (
              <Button
                key={index}
                size="sm"
                variant={index === current ? "default" : "outline"}
                onClick={() => setCurrent(index)}
              >
                {t("capture.page", { number: index + 1 })}
              </Button>
            ))}
          </div>
        )}

        {page && (
          <>
            <p className="text-xs text-muted-foreground" data-testid="crop-state">
              {t(`crop.state.${cropState(page)}`)}
            </p>
            <CropFrame
              url={url}
              page={page}
              corners={corners[current] ?? null}
              onChange={(next) =>
                setCorners((all) => all.map((value, index) => (index === current ? next : value)))
              }
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCorners((all) => all.map((value, index) => (index === current ? null : value)))
                }
                data-testid="crop-whole"
              >
                <Maximize /> {t("crop.whole")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCorners((all) =>
                    all.map((value, index) => (index === current ? startCorners(page) : value)),
                  )
                }
              >
                <RotateCcw /> {t("crop.reset")}
              </Button>
            </div>
          </>
        )}

        <DialogFooter>
          <Button onClick={() => void apply()} disabled={applying} data-testid="crop-apply">
            {applying ? t("crop.applying") : t("crop.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
